<script lang="ts" setup>
import slugify from '@/utils/slugify.ts';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  computed,
  onBeforeMount,
  watch,
} from 'vue';
import InputField from '@/components/form/Input.vue';
import MediaField from '@/components/form/MediaField.vue';
import SelectField from '@/components/form/Select.vue';
import Checkbox from '@/components/form/Checkbox.vue';
import ProductCategoryInterface from '@/interfaces/ProductCategoryInterface';
import BranchConfigurationInterface from '@/interfaces/BranchConfigurationInterface';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faTrashCan,
} from '@fortawesome/pro-solid-svg-icons';

library.add(faTrashCan);

const configuredBranch = defineModel<BranchConfigurationInterface>({
  required: true,
});
const props = defineProps<{
  index: number;
  numBranches: number;
  categories: ProductCategoryInterface[];
}>();

const emit = defineEmits<{
  delete: [number];
}>();

const categoryOptions = computed(() => props.categories.map((categ) => ({
  value: categ.id,
  label: categ.name,
})));

const dynamicTariffOptions = [
  { value: 'disabled', label: 'Dynamische Tarife nicht ausgeben' },
  { value: 'enabled', label: 'Normale und dynamische Tarife ausgeben' },
  { value: 'only', label: 'Nur dynamische Tarife ausgeben' },
];

const onUpdateCategory = (value: string) => {
  configuredBranch.value.category = props.categories.find((categ) => categ.id === parseInt(value, 10));
  configuredBranch.value.subcategories = [];
  configuredBranch.value.subcategoriesExcept = [];
  configuredBranch.value.iconOrImage = undefined;
};

watch(() => configuredBranch.value.name, (value) => {
  configuredBranch.value.slug = value;
});

watch(() => configuredBranch.value.slug, (value, oldValue) => {
  if (value !== oldValue) {
    configuredBranch.value.slug = slugify(`${value || ''}`.trim());
  }
});

const toggleSelectSubcategory = (category: {
  value: number,
  label: string
}) => {
  const isCurrentlySelected = configuredBranch.value.subcategories.filter((categ) => categ.id === category.value).length >= 1;

  if (isCurrentlySelected) {
    configuredBranch.value.subcategories = configuredBranch.value.subcategories.filter((sc) => sc.id !== category.value);
  } else {
    const mainCateg = props.categories.find((categ) => categ.id === configuredBranch.value.category?.id);
    const categToAdd = (mainCateg?.children || []).find((categ) => categ.id === category.value);

    if (categToAdd) {
      configuredBranch.value.subcategories.push(categToAdd);
    }
  }
};

const toggleSelectSubcategoryExcept = (category: {
  value: number,
  label: string
}) => {
  if (!Array.isArray(configuredBranch.value.subcategoriesExcept)) {
    configuredBranch.value.subcategoriesExcept = [];
  }

  const isCurrentlySelected = configuredBranch.value.subcategoriesExcept.filter((categ) => categ.id === category.value).length >= 1;

  if (isCurrentlySelected) {
    configuredBranch.value.subcategoriesExcept = configuredBranch.value.subcategoriesExcept.filter((sc) => sc.id !== category.value);
  } else {
    const mainCateg = props.categories.find((categ) => categ.id === configuredBranch.value.category?.id);
    const categToAdd = (mainCateg?.children || []).find((categ) => categ.id === category.value);

    if (categToAdd) {
      configuredBranch.value.subcategoriesExcept.push(categToAdd);
    }
  }
};

const subcategoryOptions = computed<{
  value: number;
  label: string;
}[]>(() => {
  const options = [] as any[];

  if (configuredBranch.value.category?.children) {
    const addWithLevel = (category: ProductCategoryInterface, level = 0) => {
      options.push({
        value: category.id,
        label: `${'-'.repeat(level)} ${category.name}`,
      });

      category.children.forEach((child) => {
        addWithLevel(child, level + 1);
      });
    };

    configuredBranch.value.category.children.forEach((categ) => {
      addWithLevel(categ);
    });
  }

  return options;
});

const canHaveDynamicTariffs = computed(() => configuredBranch.value.category?.key === 'power');

const selectableIcons = computed<ProductCategoryInterface[]>(() => [
  configuredBranch.value.category,
  ...configuredBranch.value.subcategories,
].filter((c) => c && (c?.icon || c?.image)) as ProductCategoryInterface[]);

const selectIcon = (categoryId: number) => {
  if (configuredBranch.value.iconOrImage === categoryId) {
    configuredBranch.value.iconOrImage = undefined;

    return;
  }

  configuredBranch.value.iconOrImage = categoryId;
};

onBeforeMount(() => {
  // migrate old branches
  if (!configuredBranch.value.dynamicTariffs) {
    configuredBranch.value.dynamicTariffs = 'disabled';
  }
  if (configuredBranch.value.isGlobal === undefined) {
    configuredBranch.value.isGlobal = false;
  }
});
</script>

<template>
  <div class="admin-config-branches__branch">
    <button
      v-if="numBranches > 1"
      class="delete"
      type="button"
      @click.prevent="emit('delete', index)"
    >
      <FontAwesomeIcon :icon="faTrashCan" />
    </button>
    <div
      class="name"
      style="margin-bottom: 10px"
    >
      <InputField
        label="Sparten-Name"
        v-model="configuredBranch.name"
      />
    </div>
    <div
      class="branch-flags"
      style="margin-bottom: 10px"
    >
      <Checkbox
        label="Dient lediglich zur Weiterleitung auf URL"
        v-model="configuredBranch.isUrl"
      />
      <Checkbox
        label="Branch im globalen mobilen Preisrechner anzeigen?"
        v-model="configuredBranch.isGlobal"
      />
      <InputField
        v-if="configuredBranch.isGlobal"
        type="number"
        style="min-width: 240px"
        placeholder="Position im mobilen Preisrechner"
        v-model="configuredBranch.globalPosition"
      />
    </div>
    <template v-if="!configuredBranch.isUrl">
      <div class="slug">
        <InputField
          label="Sparten-Slug"
          v-model="configuredBranch.slug"
        />
        <p style="margin-bottom: 10px;">
          Dieser Slug wird für die URL in der Preisanzeige verwendet.
        </p>
      </div>
      <div
        class="categories"
        style="margin-bottom: 10px"
      >
        <SelectField
          label="Hauptkategorie"
          :options="categoryOptions"
          :model-value="configuredBranch.category?.id"
          @update:modelValue="onUpdateCategory($event)"
        />
      </div>
      <div
        v-if="canHaveDynamicTariffs"
        class="categories-dynamic"
      >
        <SelectField
          label="Dynamische Tarife"
          :options="dynamicTariffOptions"
          v-model="configuredBranch.dynamicTariffs"
        />
      </div>
      <div
        class="subcategories"
        :key="`subcategories--${configuredBranch.category ? configuredBranch.category.id : 0}`"
      >
        <label :for="`sub-category-${configuredBranch.category?.id || '#'}`">
          Unterkategorien
        </label>
        <div
          v-for="category in subcategoryOptions"
          :key="`sub-category-${category.value}`"
          :class="{ selected: configuredBranch.subcategories.filter((categ) => categ.id === category.value).length }"
          @click="toggleSelectSubcategory(category)"
        >
          {{ category.label }}
        </div>
        <div v-if="subcategoryOptions.length === 0">
          Keine aktive Unterkategorie vorhanden.
        </div>
        <p>
          Wenn keine ausgewählt, werden alle ausgegeben
        </p>
      </div>
      <div
        class="subcategories"
        :key="`subcategories--${configuredBranch.category ? configuredBranch.category.id : 0}`"
      >
        <label :for="`sub-category-exclude-${configuredBranch.category?.id || '#'}`">
          Unterkategorien explizit ausschließen
        </label>
        <div
          v-for="category in subcategoryOptions"
          :key="`sub-category-exclude-${category.value}`"
          :class="{ selected: (configuredBranch.subcategoriesExcept || []).filter((categ) => categ.id === category.value).length }"
          @click="toggleSelectSubcategoryExcept(category)"
        >
          {{ category.label }}
        </div>
        <div v-if="subcategoryOptions.length === 0">
          Keine aktive Unterkategorie vorhanden.
        </div>
      </div>
      <div class="icons">
        <label :for="`icon-${configuredBranch.category?.id || '#'}`">
          Sparten Icon
        </label>
        <div>
          <div
            v-for="category in selectableIcons"
            :key="`${configuredBranch.id}-category-${category.id}`"
          >
            <div
              :class="{
                icon: category.icon,
                image: category.image,
                selected: category.id === configuredBranch.iconOrImage,
              }"
              :style="{ 'background-color': category.color }"
              @click="selectIcon(category.id)"
            >
              <div
                v-if="category.image"
                :style="`background-image: url(${category.image.url})`"
              />
              <div
                v-else-if="category.icon"
                :style="{ color: category.iconColor }"
              >
                <i :class="category.icon" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="!configuredBranch.iconOrImage"
        class="url"
        style="margin-top: 10px;"
      >
        <MediaField
          label="Benutzerdefiniertes Icon"
          v-model="configuredBranch.customIconOrImage"
        />
      </div>
      <div class="colors">
        <label :for="`colors-${configuredBranch.category?.id || '#'}`">
          Sparten Farben
        </label>
        <div>
          <div class="colors__color">
            <div
              class="colors__color--primary"
              :style="{
                backgroundColor: configuredBranch.customColor.primary,
              }"
            />
            <InputField
              label="Sparten Farbe primär"
              v-model="configuredBranch.customColor.primary"
            />
          </div>
          <div class="colors__color">
            <div
              class="colors__color--secondary"
              :style="{
                backgroundColor: configuredBranch.customColor.secondary,
              }"
            />
            <InputField
              label="Sparten Farbe sekundär"
              v-model="configuredBranch.customColor.secondary"
            />
          </div>
        </div>
      </div>
      <div class="images">
        <div class="images__priceshow">
          <MediaField
            label="Hero Bild Preisanzeige"
            v-model="configuredBranch.images.priceshow"
          />
        </div>
      </div>
    </template>
    <template v-if="configuredBranch.isUrl">
      <div class="url" style="margin-bottom: 10px;">
        <InputField
          label="URL"
          v-model="configuredBranch.url"
        />
      </div>
      <div class="url">
        <InputField
          label="Benutzerdefiniertes Icon (SVG Code oder Fontawesome 6)"
          v-model="configuredBranch.customIcon"
        />
      </div>
    </template>
  </div>
</template>

<style lang="scss">
@import "@/scss/component-helpers";

#io-admin {
  #admin-config-branches {
    .admin-config-branches__branch {
      padding: 10px;
      background: rgba(0, 0, 0, 0.05);
      position: relative;

      p {
        margin: 0;
      }

      & + .admin-config-branches__branch,
      & + .add-new {
        margin-top: 10px;
      }

      label {
        display: block;

        + select {
          width: 100%;
          min-width: 100%;
        }
      }

      .branch-flags {
        display: grid;
        grid-auto-columns: auto;
        grid-auto-rows: auto;
        gap: 30px;
        justify-content: start;

        > div {
          grid-column: auto/span / 1;
          grid-row: auto / 1;
        }
      }

      .subcategories {
        margin-top: 10px;

        label {
          font-weight: bold;
          margin-bottom: 5px;
        }

        > div {
          padding: 0.33333em;
          background-color: #FFFFFF;
          margin-bottom: 5px;
          border: 1px solid #8c8f94;
          border-radius: 3px;
          cursor: pointer;

          &.selected {
            background-color: lightgreen;
          }
        }
      }

      .icons {
        margin-top: 5px;

        label {
          font-weight: bold;
          margin-bottom: 10px;
        }

        > div {
          display: flex;
          flex-wrap: wrap;
          margin: -5px;

          > div {
            padding: 5px;

            > div {
              border: 1px solid #8c8f94;
              cursor: pointer;
              height: 80px;
              width: 80px;
              border-radius: 3px;
              background-size: contain;
              background-position: center;
              background-repeat: no-repeat;
              font-size: 48px;
              position: relative;

              &.icon > div {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translateX(-50%) translateY(-50%);
              }

              &.image > div {
                height: 100%;
                width: 100%;
                background-position: center;
                background-size: contain;
                background-repeat: no-repeat;
              }

              &.selected {
                border-color: green;
                border-width: 2px;
              }
            }
          }
        }
      }

      .colors {
        margin-top: 5px;

        label {
          font-weight: bold;
          margin-bottom: 10px;
        }

        > div {
          display: grid;
          grid-template-rows: auto;
          grid-auto-columns: auto;
          grid-gap: 30px;

          > div {
            &:nth-child(1) {
              grid-column: 1 / 2;
            }

            &:nth-child(2) {
              grid-column: 2 / 3;
            }
          }
        }

        &__color {
          display: grid;
          grid-template-rows: auto;
          grid-template-columns: auto 1fr;
          align-items: center;
          grid-gap: 10px;

          > div:first-child {
            grid-column: 1 / 2;
            width: 50px;
            height: 50px;
            border-radius: 50px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
          }

          > div:last-child {
            grid-column: 2 / 3;
          }
        }
      }

      .images {
        margin-top: 10px;
      }
    }
  }
}
</style>
